import { API_BASE_URL, browserSafeFetch } from "./api-base.ts";
import { sharedPublicRead } from "./public-read-cache.ts";
import type { Locale } from "./homepage-seo.ts";
import { PublicReferenceCache, publicReferenceKey, type ReferenceSnapshot } from "./public-reference-cache.ts";

export type StrategyRuleMatch = "all" | "any";
export type StrategyRuleOperator =
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "crosses_above"
  | "crosses_below"
  | "rising"
  | "falling";

export interface StrategyIndicatorOperand {
  kind: "indicator";
  id: string;
  parameters: Record<string, number>;
}

export interface StrategyNumberOperand {
  kind: "number";
  value: number;
}

export type StrategyRuleOperand = StrategyIndicatorOperand | StrategyNumberOperand;

export interface StrategyRuleCondition {
  id: string;
  left: StrategyIndicatorOperand;
  operator: StrategyRuleOperator;
  right?: StrategyRuleOperand;
}

export interface StrategyRuleGroup {
  id: "entry" | "exit";
  match: StrategyRuleMatch;
  conditions: StrategyRuleCondition[];
}

export interface StrategyRuleDSL {
  schema_version: 2;
  catalog_version: string;
  short_mode: "mirror_long";
  entry: StrategyRuleGroup;
  exit: StrategyRuleGroup;
  risk: { mode: "inherit" };
}

export interface StrategyBuilderParameterDefinition {
  id: string;
  title_key: string;
  default: number;
  minimum: number;
  maximum: number;
  step: number;
  integer: boolean;
}

export interface StrategyBuilderIndicatorDefinition {
  id: string;
  title_key: string;
  category: string;
  value_type: string;
  comparison_family: string;
  parameters: StrategyBuilderParameterDefinition[];
  supported_operators: StrategyRuleOperator[];
  numeric_range?: { minimum: number; maximum: number };
}

export interface StrategyBuilderOperatorDefinition {
  id: StrategyRuleOperator;
  arity: 1 | 2;
  title_key: string;
}

export interface StrategyBuilderTemplate {
  id: string;
  title_key: string;
  summary_key: string;
  strategy_dsl: StrategyRuleDSL;
}

export interface StrategyBuilderCatalog {
  schema_version: 2;
  catalog_version: string;
  limits: {
    max_conditions_per_group: number;
    max_total_conditions: number;
  };
  group_matches: StrategyRuleMatch[];
  short_modes: ["mirror_long"];
  indicators: StrategyBuilderIndicatorDefinition[];
  operators: StrategyBuilderOperatorDefinition[];
  templates: StrategyBuilderTemplate[];
}

export interface StrategyBuilderCapability {
  enabled: true;
  schema_versions: number[];
  catalog_versions: string[];
  latest_catalog_version: string;
  latest_catalog_url: string;
  validate_url: string;
  max_conditions_per_group: number;
  max_total_conditions: number;
  short_modes: string[];
}

export interface StrategyBuilderValidation {
  valid: true;
  canonical_dsl: StrategyRuleDSL;
  strategy_fingerprint: string;
  catalog_version: string;
  required_warmup_candles: number;
  issues: Array<{
    code?: string;
    path?: string;
    localization_key?: string;
  }>;
}

export interface StrategyBuilderTransport {
  capability(locale: Locale, force?: boolean): Promise<StrategyBuilderCapability | undefined>;
  cachedCatalog?(locale: Locale, version?: string): ReferenceSnapshot<StrategyBuilderCatalog> | undefined;
  catalog(capability: StrategyBuilderCapability, locale: Locale, version?: string, force?: boolean): Promise<StrategyBuilderCatalog>;
  validate(
    capability: StrategyBuilderCapability,
    dsl: StrategyRuleDSL,
    locale: Locale,
  ): Promise<StrategyBuilderValidation>;
}

const EXPECTED_CATALOG_PREFIX = "/api/v2/strategy-builder/catalog?version=";
const EXPECTED_VALIDATE_PATH = "/api/v2/strategy-builder/validate";
const SAFE_ID = /^[a-z][a-z0-9_]{0,63}$/;
const SAFE_RULE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,79}$/;
const SUPPORTED_OPERATORS = new Set<StrategyRuleOperator>([
  "gt", "gte", "lt", "lte", "crosses_above", "crosses_below", "rising", "falling",
]);

export function supportsStrategyBuilder(value: unknown): value is StrategyBuilderCapability {
  if (!isObject(value)) return false;
  const latest = asString(value.latest_catalog_version);
  const catalogURL = asString(value.latest_catalog_url);
  return value.enabled === true
    && Array.isArray(value.schema_versions)
    && value.schema_versions.includes(2)
    && Array.isArray(value.catalog_versions)
    && value.catalog_versions.includes(latest)
    && /^\d{4}-\d{2}-\d{2}$/.test(latest)
    && catalogURL === `${EXPECTED_CATALOG_PREFIX}${latest}`
    && value.validate_url === EXPECTED_VALIDATE_PATH
    && positiveInteger(value.max_conditions_per_group) !== undefined
    && positiveInteger(value.max_total_conditions) !== undefined
    && Number(value.max_total_conditions) >= Number(value.max_conditions_per_group)
    && Array.isArray(value.short_modes)
    && value.short_modes.includes("mirror_long");
}

export function strategyRuleDSLIdentity(value: StrategyRuleDSL | undefined): string {
  if (!value) return "";
  const operand = (item: StrategyRuleOperand | undefined): unknown => {
    if (!item) return null;
    if (item.kind === "number") return ["number", item.value];
    return [
      "indicator",
      item.id,
      Object.entries(item.parameters)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, parameter]) => [key, parameter]),
    ];
  };
  const group = (item: StrategyRuleGroup): unknown => [
    item.id,
    item.match,
    item.conditions.map((condition) => [
      condition.id,
      operand(condition.left),
      condition.operator,
      operand(condition.right),
    ]),
  ];
  return JSON.stringify([
    value.schema_version,
    value.catalog_version,
    value.short_mode,
    group(value.entry),
    group(value.exit),
    value.risk.mode,
  ]);
}

export class FetchStrategyBuilderTransport implements StrategyBuilderTransport {
  private readonly baseURL: string;
  private readonly fetcher: typeof fetch;
  private readonly cache: PublicReferenceCache;
  private cachedBlocked = false;

  constructor(baseURL = API_BASE_URL, fetcher: typeof fetch = browserSafeFetch, cache = new PublicReferenceCache()) {
    this.baseURL = baseURL;
    this.fetcher = fetcher;
    this.cache = cache;
  }

  async capability(locale: Locale, force = false): Promise<StrategyBuilderCapability | undefined> {
    const response = await this.request("/api/capabilities", { method: "GET" }, locale, force);
    const candidate = isObject(response) && isObject(response.capabilities)
      ? response.capabilities.strategy_builder
      : undefined;
    if (!supportsStrategyBuilder(candidate)) {
      // A fresh disabled/unsupported response wins over every previously saved catalog.
      this.cachedBlocked = true;
      this.cache.removeForOrigin("builder-current", this.baseURL);
      this.cache.removeForOrigin("builder-catalog", this.baseURL);
      return undefined;
    }
    this.cachedBlocked = false;
    return candidate;
  }

  cachedCatalog(locale: Locale, version?: string): ReferenceSnapshot<StrategyBuilderCatalog> | undefined {
    if (this.cachedBlocked) return undefined;
    const pinned = version ?? this.cache.read(
      publicReferenceKey("builder-current", this.baseURL, [locale]),
      (value) => {
        if (!isObject(value) || !/^\d{4}-\d{2}-\d{2}$/.test(asString(value.catalogVersion))) {
          throw new Error("invalid_strategy_builder_cache_pointer");
        }
        return asString(value.catalogVersion);
      },
    )?.value;
    if (!pinned || !/^\d{4}-\d{2}-\d{2}$/.test(pinned)) return undefined;
    return this.cache.read(
      publicReferenceKey("builder-catalog", this.baseURL, [locale, 2, pinned]),
      (value) => parseCachedStrategyBuilderCatalog(value, pinned),
    );
  }

  async catalog(
    capability: StrategyBuilderCapability,
    locale: Locale,
    version = capability.latest_catalog_version,
    force = false,
  ): Promise<StrategyBuilderCatalog> {
    if (!supportsStrategyBuilder(capability) || !/^\d{4}-\d{2}-\d{2}$/.test(version)
      || !capability.catalog_versions.includes(version)) {
      throw new Error("strategy_builder_unsupported_catalog");
    }
    const raw = await this.request(`${EXPECTED_CATALOG_PREFIX}${encodeURIComponent(version)}`, { method: "GET" }, locale, force,
      (value) => parseStrategyBuilderCatalog(value, capability, version));
    const catalog = parseStrategyBuilderCatalog(raw, capability, version);
    const retention = 30 * 24 * 60 * 60 * 1000;
    // Only parsed, public catalog fields persist. Never store the capability response.
    this.cache.write(publicReferenceKey("builder-catalog", this.baseURL, [locale, 2, version]), catalog, retention);
    if (version === capability.latest_catalog_version) {
      this.cache.write(publicReferenceKey("builder-current", this.baseURL, [locale]), { catalogVersion: version }, retention);
    }
    return catalog;
  }

  async validate(
    capability: StrategyBuilderCapability,
    dsl: StrategyRuleDSL,
    locale: Locale,
  ): Promise<StrategyBuilderValidation> {
    if (!supportsStrategyBuilder(capability) || !capability.catalog_versions.includes(dsl.catalog_version)) {
      throw new Error("strategy_builder_unavailable");
    }
    const raw = await this.request(capability.validate_url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ strategy_dsl: dsl }),
    }, locale);
    return parseStrategyBuilderValidation(raw, capability, dsl.catalog_version);
  }

  private async request(path: string, init: RequestInit, locale: Locale, force = false,
    parse?: (value: unknown) => unknown): Promise<unknown> {
    const base = new URL(`${this.baseURL.replace(/\/$/, "")}/`);
    const target = new URL(path, base);
    if (target.origin !== base.origin) throw new Error("unsafe_strategy_builder_origin");
    const headers = new Headers(init.headers);
    headers.set("Accept-Language", locale);
    const read = async (signal?: AbortSignal): Promise<unknown> => {
      const response = await this.fetcher(target, { ...init, headers, signal });
      if (!response.ok) throw new Error(`strategy_builder_request_failed:${response.status}`);
      const value: unknown = await response.json();
      return parse ? parse(value) : value;
    };
    return init.method === "GET"
      ? sharedPublicRead(path, locale, read, { baseURL: this.baseURL, fetcher: this.fetcher, force })
      : read(init.signal ?? undefined);
  }
}

export function parseStrategyBuilderCatalog(
  value: unknown,
  capability: StrategyBuilderCapability,
  version = capability.latest_catalog_version,
): StrategyBuilderCatalog {
  if (!supportsStrategyBuilder(capability)
    || !/^\d{4}-\d{2}-\d{2}$/.test(version)
    || !capability.catalog_versions.includes(version)
    || !isObject(value)
    || value.schema_version !== 2
    || value.catalog_version !== version
    || !isObject(value.limits)
    || positiveInteger(value.limits.max_conditions_per_group) !== capability.max_conditions_per_group
    || positiveInteger(value.limits.max_total_conditions) !== capability.max_total_conditions
    || !Array.isArray(value.group_matches)
    || value.group_matches.length !== 2
    || !value.group_matches.includes("all")
    || !value.group_matches.includes("any")
    || !Array.isArray(value.short_modes)
    || value.short_modes.length !== 1
    || value.short_modes[0] !== "mirror_long"
    || !Array.isArray(value.indicators)
    || !Array.isArray(value.operators)
    || !Array.isArray(value.templates)) {
    throw new Error("invalid_strategy_builder_catalog");
  }

  const indicators = value.indicators.map(parseIndicator);
  const indicatorIDs = new Set(indicators.map((item) => item.id));
  if (indicatorIDs.size !== indicators.length || !indicators.length) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  const operators = value.operators.map(parseOperator);
  const operatorIDs = new Set(operators.map((item) => item.id));
  if (operatorIDs.size !== operators.length || !operators.length) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  for (const indicator of indicators) {
    if (!indicator.supported_operators.every((operator) => operatorIDs.has(operator))) {
      throw new Error("invalid_strategy_builder_catalog");
    }
  }

  const catalog: StrategyBuilderCatalog = {
    schema_version: 2,
    catalog_version: version,
    limits: {
      max_conditions_per_group: capability.max_conditions_per_group,
      max_total_conditions: capability.max_total_conditions,
    },
    group_matches: ["all", "any"],
    short_modes: ["mirror_long"],
    indicators,
    operators,
    templates: [],
  };
  catalog.templates = value.templates.map((template) => parseTemplate(template, catalog));
  if (!catalog.templates.length
    || new Set(catalog.templates.map((item) => item.id)).size !== catalog.templates.length) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  return catalog;
}

export function parseCachedStrategyBuilderCatalog(value: unknown, version: string): StrategyBuilderCatalog {
  if (!isObject(value) || !isObject(value.limits)) throw new Error("invalid_strategy_builder_catalog");
  // This local manifest supplies parsing constraints, NOT server availability or readiness.
  return parseStrategyBuilderCatalog(value, {
    enabled: true, schema_versions: [2], catalog_versions: [version],
    latest_catalog_version: version, latest_catalog_url: `${EXPECTED_CATALOG_PREFIX}${version}`,
    validate_url: EXPECTED_VALIDATE_PATH,
    max_conditions_per_group: Number(value.limits.max_conditions_per_group),
    max_total_conditions: Number(value.limits.max_total_conditions), short_modes: ["mirror_long"],
  });
}

function parseIndicator(value: unknown): StrategyBuilderIndicatorDefinition {
  if (!isObject(value)
    || !SAFE_ID.test(asString(value.id))
    || !asString(value.title_key)
    || !asString(value.category)
    || !asString(value.value_type)
    || !asString(value.comparison_family)
    || !Array.isArray(value.parameters)
    || !Array.isArray(value.supported_operators)) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  const parameters = value.parameters.map((parameter) => {
    if (!isObject(parameter)
      || !SAFE_ID.test(asString(parameter.id))
      || !asString(parameter.title_key)
      || !finiteNumber(parameter.default)
      || !finiteNumber(parameter.minimum)
      || !finiteNumber(parameter.maximum)
      || !finiteNumber(parameter.step)
      || parameter.integer !== true
      || Number(parameter.minimum) > Number(parameter.default)
      || Number(parameter.default) > Number(parameter.maximum)
      || Number(parameter.step) <= 0) {
      throw new Error("invalid_strategy_builder_catalog");
    }
    return {
      id: asString(parameter.id),
      title_key: asString(parameter.title_key),
      default: Number(parameter.default),
      minimum: Number(parameter.minimum),
      maximum: Number(parameter.maximum),
      step: Number(parameter.step),
      integer: true,
    };
  });
  if (new Set(parameters.map((item) => item.id)).size !== parameters.length) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  const supported = value.supported_operators.map(asString);
  if (!supported.length || supported.some((item) => !SUPPORTED_OPERATORS.has(item as StrategyRuleOperator))) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  let numericRange: StrategyBuilderIndicatorDefinition["numeric_range"];
  if (value.numeric_range !== undefined) {
    if (!isObject(value.numeric_range)
      || !finiteNumber(value.numeric_range.minimum)
      || !finiteNumber(value.numeric_range.maximum)
      || Number(value.numeric_range.minimum) > Number(value.numeric_range.maximum)) {
      throw new Error("invalid_strategy_builder_catalog");
    }
    numericRange = {
      minimum: Number(value.numeric_range.minimum),
      maximum: Number(value.numeric_range.maximum),
    };
  }
  return {
    id: asString(value.id),
    title_key: asString(value.title_key),
    category: asString(value.category),
    value_type: asString(value.value_type),
    comparison_family: asString(value.comparison_family),
    parameters,
    supported_operators: supported as StrategyRuleOperator[],
    numeric_range: numericRange,
  };
}

function parseOperator(value: unknown): StrategyBuilderOperatorDefinition {
  if (!isObject(value)
    || !SUPPORTED_OPERATORS.has(asString(value.id) as StrategyRuleOperator)
    || (value.arity !== 1 && value.arity !== 2)
    || !asString(value.title_key)) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  return {
    id: asString(value.id) as StrategyRuleOperator,
    arity: value.arity,
    title_key: asString(value.title_key),
  };
}

function parseTemplate(
  value: unknown,
  catalog: StrategyBuilderCatalog,
): StrategyBuilderTemplate {
  if (!isObject(value)
    || !SAFE_ID.test(asString(value.id))
    || !asString(value.title_key)
    || !asString(value.summary_key)) {
    throw new Error("invalid_strategy_builder_catalog");
  }
  const dsl = parseStrategyRuleDSL(value.strategy_dsl, catalog);
  return {
    id: asString(value.id),
    title_key: asString(value.title_key),
    summary_key: asString(value.summary_key),
    strategy_dsl: dsl,
  };
}

export function parseStrategyRuleDSL(
  value: unknown,
  catalog: StrategyBuilderCatalog,
): StrategyRuleDSL {
  if (!isObject(value)
    || value.schema_version !== 2
    || value.catalog_version !== catalog.catalog_version
    || value.short_mode !== "mirror_long"
    || !isObject(value.risk)
    || value.risk.mode !== "inherit") {
    throw new Error("invalid_strategy_builder_dsl");
  }
  const seen = new Set<string>();
  const entry = parseGroup(value.entry, "entry", catalog, seen);
  const exit = parseGroup(value.exit, "exit", catalog, seen);
  if (!entry.conditions.length
    || entry.conditions.length > catalog.limits.max_conditions_per_group
    || exit.conditions.length > catalog.limits.max_conditions_per_group
    || entry.conditions.length + exit.conditions.length > catalog.limits.max_total_conditions) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  return {
    schema_version: 2,
    catalog_version: catalog.catalog_version,
    short_mode: "mirror_long",
    entry,
    exit,
    risk: { mode: "inherit" },
  };
}

function parseGroup(
  value: unknown,
  id: "entry" | "exit",
  catalog: StrategyBuilderCatalog,
  seen: Set<string>,
): StrategyRuleGroup {
  if (!isObject(value)
    || value.id !== id
    || (value.match !== "all" && value.match !== "any")
    || !Array.isArray(value.conditions)) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  if (seen.has(id)) throw new Error("invalid_strategy_builder_dsl");
  seen.add(id);
  return {
    id,
    match: value.match,
    conditions: value.conditions.map((condition) => parseCondition(condition, catalog, seen)),
  };
}

function parseCondition(
  value: unknown,
  catalog: StrategyBuilderCatalog,
  seen: Set<string>,
): StrategyRuleCondition {
  if (!isObject(value)
    || !SAFE_RULE_ID.test(asString(value.id))
    || seen.has(asString(value.id))
    || !SUPPORTED_OPERATORS.has(asString(value.operator) as StrategyRuleOperator)) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  seen.add(asString(value.id));
  const left = parseOperand(value.left, catalog, false) as StrategyIndicatorOperand;
  const operator = asString(value.operator) as StrategyRuleOperator;
  const operatorDefinition = catalog.operators.find((item) => item.id === operator);
  const indicator = catalog.indicators.find((item) => item.id === left.id);
  if (!operatorDefinition || !indicator?.supported_operators.includes(operator)) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  if (operatorDefinition.arity === 1) {
    if ("right" in value) throw new Error("invalid_strategy_builder_dsl");
    return { id: asString(value.id), left, operator };
  }
  const right = parseOperand(value.right, catalog, true);
  if (right.kind === "indicator") {
    const rightDefinition = catalog.indicators.find((item) => item.id === right.id);
    if (!rightDefinition
      || rightDefinition.value_type !== indicator.value_type
      || rightDefinition.comparison_family !== indicator.comparison_family
      || JSON.stringify(right) === JSON.stringify(left)) {
      throw new Error("invalid_strategy_builder_dsl");
    }
  } else if (indicator.numeric_range
    && (right.value < indicator.numeric_range.minimum || right.value > indicator.numeric_range.maximum)) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  return { id: asString(value.id), left, operator, right };
}

function parseOperand(
  value: unknown,
  catalog: StrategyBuilderCatalog,
  allowNumber: boolean,
): StrategyRuleOperand {
  if (!isObject(value)) throw new Error("invalid_strategy_builder_dsl");
  if (value.kind === "number") {
    if (!allowNumber || !finiteNumber(value.value)) throw new Error("invalid_strategy_builder_dsl");
    return { kind: "number", value: Number(value.value) };
  }
  if (value.kind !== "indicator" || !isObject(value.parameters)) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  const definition = catalog.indicators.find((item) => item.id === value.id);
  if (!definition) throw new Error("invalid_strategy_builder_dsl");
  const parameters: Record<string, number> = {};
  for (const parameter of definition.parameters) {
    const raw = value.parameters[parameter.id];
    if (!Number.isInteger(raw)
      || Number(raw) < parameter.minimum
      || Number(raw) > parameter.maximum) {
      throw new Error("invalid_strategy_builder_dsl");
    }
    parameters[parameter.id] = Number(raw);
  }
  if (Object.keys(value.parameters).length !== definition.parameters.length) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  if (definition.id === "macd_histogram" && parameters.fast >= parameters.slow) {
    throw new Error("invalid_strategy_builder_dsl");
  }
  return { kind: "indicator", id: definition.id, parameters };
}

export function parseStrategyBuilderValidation(
  value: unknown,
  capability: StrategyBuilderCapability,
  version = capability.latest_catalog_version,
): StrategyBuilderValidation {
  if (!supportsStrategyBuilder(capability) || !capability.catalog_versions.includes(version)
    || !isObject(value)
    || value.valid !== true
    || value.catalog_version !== version
    || !asString(value.strategy_fingerprint)
    || positiveIntegerOrZero(value.required_warmup_candles) === undefined
    || !Array.isArray(value.issues)
    || !isObject(value.canonical_dsl)
    || value.canonical_dsl.schema_version !== 2
    || value.canonical_dsl.catalog_version !== version) {
    throw new Error("invalid_strategy_builder_validation");
  }
  return value as unknown as StrategyBuilderValidation;
}

export function indicatorOperand(
  definition: StrategyBuilderIndicatorDefinition,
): StrategyIndicatorOperand {
  return {
    kind: "indicator",
    id: definition.id,
    parameters: Object.fromEntries(
      definition.parameters.map((parameter) => [parameter.id, parameter.default]),
    ),
  };
}

export function compatibleRightIndicators(
  catalog: StrategyBuilderCatalog,
  left: StrategyIndicatorOperand,
): StrategyBuilderIndicatorDefinition[] {
  const definition = catalog.indicators.find((item) => item.id === left.id);
  if (!definition) return [];
  return catalog.indicators.filter((candidate) => (
    (candidate.id !== definition.id || indicatorCanUseDifferentParameters(candidate))
    && candidate.value_type === definition.value_type
    && candidate.comparison_family === definition.comparison_family
  ));
}

export function distinctRightIndicatorOperand(
  catalog: StrategyBuilderCatalog,
  left: StrategyIndicatorOperand,
  candidate: StrategyIndicatorOperand,
): StrategyIndicatorOperand {
  if (candidate.id !== left.id || strategyRuleDSLParameterIdentity(candidate) !== strategyRuleDSLParameterIdentity(left)) {
    return candidate;
  }
  const definition = catalog.indicators.find((item) => item.id === candidate.id);
  if (!definition) return candidate;
  for (const parameter of definition.parameters) {
    const current = candidate.parameters[parameter.id];
    const higher = current + parameter.step;
    const lower = current - parameter.step;
    if (higher <= parameter.maximum) {
      return {
        ...candidate,
        parameters: { ...candidate.parameters, [parameter.id]: higher },
      };
    }
    if (lower >= parameter.minimum) {
      return {
        ...candidate,
        parameters: { ...candidate.parameters, [parameter.id]: lower },
      };
    }
  }
  return candidate;
}

export function nextCondition(
  group: "entry" | "exit",
  catalog: StrategyBuilderCatalog,
  usedIDs: ReadonlySet<string>,
): StrategyRuleCondition {
  const close = catalog.indicators.find((item) => item.id === "close") ?? catalog.indicators[0];
  const rightDefinition = compatibleRightIndicators(catalog, indicatorOperand(close))[0];
  if (!close || !rightDefinition) throw new Error("strategy_builder_catalog_has_no_default_pair");
  let suffix = 1;
  while (usedIDs.has(`${group}.rule.${suffix}`)) suffix += 1;
  return {
    id: `${group}.rule.${suffix}`,
    left: indicatorOperand(close),
    operator: close.supported_operators.includes("gt") ? "gt" : close.supported_operators[0],
    right: indicatorOperand(rightDefinition),
  };
}

export function cloneStrategyRuleDSL(value: StrategyRuleDSL): StrategyRuleDSL {
  return JSON.parse(JSON.stringify(value)) as StrategyRuleDSL;
}

function isObject(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function positiveInteger(value: unknown): number | undefined {
  return Number.isInteger(value) && Number(value) > 0 ? Number(value) : undefined;
}

function positiveIntegerOrZero(value: unknown): number | undefined {
  return Number.isInteger(value) && Number(value) >= 0 ? Number(value) : undefined;
}

function indicatorCanUseDifferentParameters(
  definition: StrategyBuilderIndicatorDefinition,
): boolean {
  return definition.parameters.some((parameter) => parameter.maximum > parameter.minimum);
}

function strategyRuleDSLParameterIdentity(operand: StrategyIndicatorOperand): string {
  return JSON.stringify(
    Object.entries(operand.parameters).sort(([left], [right]) => left.localeCompare(right)),
  );
}
